import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/lib/UI/loadingSpinner/LoadingSpinner';
import { Alert, Box, Chip } from '@mui/material';
import { useAppSelector } from 'setup/store';
import { useDayReportEntryActions } from 'actions/day-report-entry-actions';
import { IDateFilter, IPageData } from 'common/interfaces';
import { IDayReport } from 'common/interfaces/iDayReports';
import UITable, { DisplayItem, RowItem } from 'components/lib/UI/table/UITable';
import ReportSearchFilter from 'components/report-search-filter/ReportSearchFilter';
import { IStore } from 'common/interfaces/istore';
import { useNavigate } from 'react-router-dom';
import { getDayReportUpdateDate, getRoyaltyReportUpdateDate } from 'common/utils/day-report';
import { getDisplayDate } from 'common/utils/date-util';
import { useRoyaltyReportActions } from 'actions/royalty-report-actions';
import { IRoyaltyReport } from 'common/interfaces/iRoyaltyReports';
import { formatDecimal, formatDecimalNumber } from 'common/utils';
import { formatCurrency } from 'common/utils/string-utils';
import moment from 'moment';

const DEFAULT_HEADERS = [
  {
    label: '#',
  },
  {
    label: 'Week Ending Date',
  },
  {
    label: 'Status',
  },
  {
    label: 'Total Amount',
  },
  {
    label: 'Updated Time',
  },
] as DisplayItem[];

const RoyaltyReportsPage = () => {
  const { t } = useTranslation();
  const appConfig = useAppSelector((state) => state.appReducer.appConfig);
  const navigate = useNavigate();
  const error = useAppSelector((state) => state.royaltyReportReducer.error);
  const isLoading = useAppSelector((state) => state.royaltyReportReducer.loading);
  const reports = useAppSelector((state) => state.royaltyReportReducer.reports);
  const [_selectedStore, setSelectedStore] = useState<IStore>();
  const [_selectedDateFilter, setSelectedDateFilter] = useState<IDateFilter>();
  const [headers, setHaders] = useState<DisplayItem[]>(DEFAULT_HEADERS);

  // const { setSelectedStore } = useSessionActions();
  const { loadRoyaltyReports, resetContext, setOngoingRoyaltyReport } = useRoyaltyReportActions();

  useEffect(() => {
    resetContext();
  }, []);

  const _loadRoyaltyReports = (
    df: IDateFilter,
    selectedStore: IStore | undefined | null,
    pageNo: number,
    pageSize: number,
  ) => {
    setOngoingRoyaltyReport(undefined);
    return loadRoyaltyReports(selectedStore?.id || -1, df.startDate, df.endDate, pageNo, pageSize);
  };

  function _fetchMoreData(pageNo: number, pageSize: number): Promise<IPageData<IRoyaltyReport> | undefined> {
    console.log('_fetchMoreData:::', pageNo, pageSize);
    return _selectedDateFilter
      ? _loadRoyaltyReports(_selectedDateFilter, _selectedStore, pageNo, pageSize) || Promise.resolve(undefined)
      : Promise.resolve(undefined);
  }

  const _onSearchReport = async (dateFilter: IDateFilter, store: IStore | undefined | null) => {
    setSelectedDateFilter(dateFilter);
    await _loadRoyaltyReports(dateFilter, store, 1, appConfig.defaultListSize);
    setSelectedStore(store || undefined);
  };

  const keyExtractor = (item: IRoyaltyReport, index: number) => {
    return (item.id || index).toString();
  };

  const _handleRowSelection = (item: IRoyaltyReport) => {
    if (item) {
      navigate('/form-responses/' + item.id);
    }
  };

  const _renderRow = (item: IRoyaltyReport, index: number): RowItem => {
    const row = {
      id: item.id,
      index,
      data: [
        {
          label: index + 1 + '',
        },
        {
          label: (getDisplayDate(item.reportDate) || '-') + '',
        },
        {
          // label: item.status + '',
          icon: <Chip label={item.status} color={item.status === 'APPROVED' ? 'success' : 'primary'} />,
        },
        {
          label: formatCurrency(item.totalAmount, 'currency', 2) + '',
        },
        {
          label: (getRoyaltyReportUpdateDate(item, true) || '-') + '',
        },
      ],
    };
    if (item.storeName) {
      row.data.push({ label: item.storeName });
    } else if (_selectedStore && _selectedStore.id === item.storeId) {
      row.data.push({ label: '#' + _selectedStore.storeNumber });
    }
    return row;
  };
  return (
    <section>
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} className="pageTitleTitleContainer">
        <h2 className="pageTitle">{t('page.royaltyReports')}</h2>
      </Box>
      <ReportSearchFilter handleSubmit={_onSearchReport} currentDate={moment().add(7, 'days').toDate()} duration={10} />
      <div style={{ margin: '20px 0px' }}>
        {/* {isLoading ? <LoadingSpinner /> : null} */}
        {reports && reports.totalItems > 0 ? (
          <UITable<IRoyaltyReport>
            pagedData={reports}
            keyExtractor={keyExtractor}
            pageSize={appConfig.defaultListSize}
            loadData={_fetchMoreData}
            extractRowItem={_renderRow}
            headers={headers}
            isLoading={isLoading}
            onItemPressed={_handleRowSelection}
            title={_selectedStore?.storeNumber ? 'Reports for Store: ' + _selectedStore?.storeNumber : ''}
            hideActions
          />
        ) : reports?.totalItems === 0 ? (
          <Alert severity="warning">No Report found</Alert>
        ) : !isLoading ? (
          <Alert severity="info">Select store and date range to search for reports.</Alert>
        ) : null}
      </div>
    </section>
  );
};

export default RoyaltyReportsPage;
